/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { Injectable } from '@angular/core';
import { completedClusterAction } from '@app/common/rxjs-operators/completed-cluster-action';
import { CacheConfiguration } from '@shared/types/cache';
import { ActionName } from '@shared/types/cluster-actions';
import { IgniteCache } from '@shared/types/common';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ClusterActionService } from './cluster-action.service';
import { WebSocketConnection } from './websocket-connection.service';

@Injectable({ providedIn: 'root' })
export class CacheApi {
  constructor(
    private ws: WebSocketConnection,
    private clusterActions: ClusterActionService,
  ) {}

  getCaches = (clusterId: string): Observable<IgniteCache[]> => this.ws.getTopic(`/topic/cluster/caches/${clusterId}`);
  getCachePartitions = (clusterId: string, cacheName: string) =>
    this.clusterActions.dispatch(ActionName.CACHE_PARTITIONS, { nodeIds: [], clusterId, argument: { cacheName } });

  getCacheConfiguration(cacheName: string, clusterId: string): Observable<CacheConfiguration> {
    return this.clusterActions.dispatch(ActionName.CACHE_CONFIGURATION, { argument: { cacheName }, clusterId }).pipe(
      completedClusterAction(),
      map(({ result }) => JSON.parse(result.json)),
    );
  }

  clearCache(cacheName: string, clusterId: string) {
    return this.clusterActions
      .dispatch(ActionName.CACHE_CLEAR, { argument: { cacheName }, clusterId })
      .pipe(completedClusterAction());
  }

  rebalanceCache(cacheName: string, clusterId: string) {
    return this.clusterActions
      .dispatch(ActionName.CACHE_REBALANCE, { argument: { cacheName }, clusterId })
      .pipe(completedClusterAction());
  }

  enabledCacheStatistics(cacheNames: string[], clusterId: string) {
    return this.clusterActions
      .dispatch(ActionName.CACHE_ENABLE_STATISTICS, { argument: { cacheNames }, clusterId })
      .pipe(completedClusterAction());
  }

  disableCacheStatistics(cacheNames: string[], clusterId: string) {
    return this.clusterActions
      .dispatch(ActionName.CACHE_DISABLE_STATISTICS, { argument: { cacheNames }, clusterId })
      .pipe(completedClusterAction());
  }

  destroyCache(cacheName: string, clusterId: string) {
    return this.clusterActions
      .dispatch(ActionName.CACHE_DESTROY, { argument: { cacheName }, clusterId })
      .pipe(completedClusterAction());
  }

  loadCacheFromCacheStore(cacheName: string, clusterId: string) {
    return this.clusterActions
      .dispatch(ActionName.CACHE_LOAD_FROM_STORE, { argument: { cacheName }, clusterId })
      .pipe(completedClusterAction());
  }

  pauseDataReplication(cacheNames: string[], clusterId: string) {
    return this.clusterActions
      .dispatch(ActionName.PAUSE_CACHE_DATA_REPLICATION, { argument: { cacheNames }, clusterId })
      .pipe(completedClusterAction());
  }

  resumeDataReplication(cacheNames: string[], clusterId: string) {
    return this.clusterActions
      .dispatch(ActionName.RESUME_CACHE_DATA_REPLICATION, { argument: { cacheNames }, clusterId })
      .pipe(completedClusterAction());
  }

  startFullStateTransfer(cacheNames: string[], clusterId: string) {
    return this.clusterActions
      .dispatch(ActionName.START_CACHE_FST, { argument: { cacheNames }, clusterId })
      .pipe(completedClusterAction());
  }

  stopFullStateTransfer(cacheNames: string[], clusterId: string) {
    return this.clusterActions
      .dispatch(ActionName.STOP_CACHE_FST, { argument: { cacheNames }, clusterId })
      .pipe(completedClusterAction());
  }
}
